import type { VoucherResponse } from '@/api/model/voucherResponse';
import { VoucherStatus } from '@/api/model/voucherStatus';
import { useListProviders } from '@/api/voucher-providers/voucher-providers';
import { useListVouchers } from '@/api/vouchers/vouchers';
import { VoucherCard } from '@/components/vouchers/VoucherCard';
import { VoucherGridBase } from '@/components/vouchers/VoucherGridBase';
import type { VoucherSortOrder } from '@/components/vouchers/VoucherFilterControls';
import React, { useMemo, useState } from 'react';

type VoucherGridContainerProps = {
  selectedCategory: string;
  searchQuery: string;
  sortOrder: VoucherSortOrder;
  onVoucherClick: (voucher: VoucherResponse) => void;
};

export const VoucherGridContainer: React.FC<VoucherGridContainerProps> = ({
  selectedCategory,
  searchQuery,
  sortOrder,
  onVoucherClick,
}) => {
  const [currentPage, setCurrentPage] = useState(1);

  // Get providers for category filtering
  const { data: providersData } = useListProviders({
    is_active: true,
    per_page: 100, // Get all active providers
  });

  // Filter provider IDs by category
  const categoryProviderIds = useMemo(() => {
    if (selectedCategory === 'alle' || !providersData?.items) {
      return undefined;
    }

    return providersData.items.filter(provider => provider.category === selectedCategory).map(provider => provider.id);
  }, [selectedCategory, providersData]);

  // Build voucher query parameters
  const voucherParams = useMemo(() => {
    // For category filtering, we need to fetch more data to do client-side pagination
    // For "alle" category, we can use server-side pagination
    const needsClientSidePagination = selectedCategory !== 'alle';

    const params = {
      status: VoucherStatus.PUBLISHED,
      is_active: true,
      page: needsClientSidePagination ? 1 : currentPage,
      per_page: needsClientSidePagination ? 100 : 6,
      search: searchQuery || undefined,
      sort_order: sortOrder,
    };

    // If a specific category is selected and we have provider IDs, filter by the first one
    // Note: The API only supports one provider_id, so this is a limitation
    if (categoryProviderIds && categoryProviderIds.length > 0) {
      // For now, we'll need to filter client-side or modify the API to support multiple provider_ids
      // This is a limitation of the current API design
    }

    return params;
  }, [searchQuery, currentPage, categoryProviderIds, selectedCategory, sortOrder]);

  const { data: vouchersData, isLoading, isError, error } = useListVouchers(voucherParams);

  // Filter vouchers by category client-side since API doesn't support multiple provider_ids
  const filteredVouchers = useMemo(() => {
    const allVouchers = vouchersData?.items || [];

    if (selectedCategory === 'alle') {
      return allVouchers;
    }

    return allVouchers.filter(voucher => voucher.provider.category === selectedCategory);
  }, [vouchersData?.items, selectedCategory]);

  // Use API pagination data when available, fallback to client-side pagination
  const totalVouchers = selectedCategory === 'alle' ? vouchersData?.total || 0 : filteredVouchers.length;
  const totalPages =
    selectedCategory === 'alle' ? vouchersData?.pages || 1 : Math.max(1, Math.ceil(filteredVouchers.length / 6));

  // For category filtering, use client-side pagination; for "alle", use server-side
  const sortedVouchers = useMemo(() => {
    const vouchers = selectedCategory === 'alle' ? vouchersData?.items || [] : filteredVouchers;

    return [...vouchers].sort((a, b) => {
      const aTime = Date.parse(a.created_at);
      const bTime = Date.parse(b.created_at);

      if (Number.isNaN(aTime) && Number.isNaN(bTime)) {
        return 0;
      }

      if (Number.isNaN(aTime)) {
        return sortOrder === 'newest' ? 1 : -1;
      }

      if (Number.isNaN(bTime)) {
        return sortOrder === 'newest' ? -1 : 1;
      }

      return sortOrder === 'newest' ? bTime - aTime : aTime - bTime;
    });
  }, [filteredVouchers, selectedCategory, sortOrder, vouchersData?.items]);

  const displayVouchers =
    selectedCategory === 'alle' ? sortedVouchers : sortedVouchers.slice((currentPage - 1) * 6, currentPage * 6);

  const goToPage = (page: number) => setCurrentPage(Math.min(Math.max(1, page), totalPages));

  // Reset to page 1 when filters change
  React.useEffect(() => {
    setCurrentPage(1);
  }, [selectedCategory, searchQuery, sortOrder]);

  const emptyMessage =
    selectedCategory !== 'alle' || searchQuery
      ? 'Für diese Filter wurden keine Gutscheine gefunden. Versuchen Sie andere Suchkriterien.'
      : 'Aktuell sind keine Gutscheine verfügbar. Schauen Sie später noch einmal vorbei!';

  return (
    <VoucherGridBase
      items={displayVouchers}
      isLoading={isLoading}
      isError={isError}
      error={error}
      emptyMessage={emptyMessage}
      currentPage={currentPage}
      totalPages={totalPages}
      totalVouchers={totalVouchers}
      onPageChange={goToPage}
      renderItem={voucher => <VoucherCard key={voucher.id} voucher={voucher} onClick={() => onVoucherClick(voucher)} />}
    />
  );
};
